import { useMemo } from 'react';
import type { PtoPprFact, PtoPprTkDraft } from '@shared/api';
import {
  Banner,
  DataTable,
  KvGrid,
  Muted,
  Stat,
  Stats,
  TableWrap,
  Tag,
  fmtDate,
  fmtNum,
} from '../kfo/ui';
import { AiMarkTag } from './AiMark';
import {
  CALC_KIND_LABEL,
  CALC_STATUS,
  FACT_KIND_LABEL,
  FACT_KIND_ORDER,
  aiDetails,
  equipmentKindLabel,
  factSource,
  plural,
  pointsWord,
} from './model';
import { FactItem, Quote, SubTitle } from './ui';

function Fact({ fact }: { fact: PtoPprFact }) {
  return (
    <FactItem data-fact={fact.id}>
      <code>{fact.id}</code>
      {fact.text}
      <Muted>{factSource(fact)}</Muted>
      {fact.quote && (
        <details>
          <summary>Цитата из РД</summary>
          <Quote>«{fact.quote}»</Quote>
        </details>
      )}
    </FactItem>
  );
}

/**
 * Вкладка «Паспорт РД»: на что опирается текст — сведения из РД с дословными
 * цитатами (сверены кодом), объёмы по РД, расчёты кодом, разрешённые нормативы
 * и исходные данные мастера.
 */
export function PassportTab({ draft }: { draft: PtoPprTkDraft }) {
  const facts = draft.facts;
  const inputs = draft.inputs;
  const stats = facts?.stats;

  const groups = useMemo(() => {
    const byKind = new Map<string, PtoPprFact[]>();
    for (const f of facts?.items ?? []) byKind.set(f.kind, [...(byKind.get(f.kind) ?? []), f]);
    const known = FACT_KIND_ORDER.filter((k) => byKind.has(k));
    const other = [...byKind.keys()].filter((k) => !FACT_KIND_ORDER.includes(k));
    return [...known, ...other].map((k) => [k, byKind.get(k)!] as const);
  }, [facts]);

  return (
    <div>
      <Stats>
        <Stat>
          <b>{facts?.items?.length ?? 0}</b>
          <span>сведений из РД</span>
        </Stat>
        <Stat>
          <b>{facts?.volumes?.length ?? 0}</b>
          <span>строк объёмов</span>
        </Stat>
        <Stat>
          <b>{facts?.calcs?.length ?? 0}</b>
          <span>расчётов</span>
        </Stat>
        <Stat>
          <b>{facts?.norms?.length ?? 0}</b>
          <span>нормативов</span>
        </Stat>
      </Stats>
      {stats && (
        <Muted data-ppr="facts-stats">
          Изучено фрагментов {stats.chunks_used} из {stats.chunks_total}, отброшено сведений без
          подтверждения цитатой: {stats.facts_dropped} (предложено моделью {stats.facts_proposed},{' '}
          {stats.windows} {plural(stats.windows, ['окно', 'окна', 'окон'])} чтения).
        </Muted>
      )}
      {(stats?.windows_failed ?? 0) > 0 && (
        <Banner $tone="danger" data-ppr="facts-windows-failed">
          Не изучено частей РД: {stats?.windows_failed} из {stats?.windows} (сбой ИИ) — паспорт
          неполон, документ не будет утверждён. Перезапустите генерацию.
        </Banner>
      )}

      <SubTitle>Объект</SubTitle>
      {facts?.object ? (
        <Fact fact={facts.object} />
      ) : (
        <Muted>Наименование объекта в РД не найдено.</Muted>
      )}

      <details>
        <summary>
          <b>Исходные данные мастера</b>
        </summary>
        <KvGrid>
          <dt>Объект (указан инженером)</dt>
          <dd>{inputs?.object_name || '—'}</dd>
          <dt>Заказчик</dt>
          <dd>{inputs?.customer || '—'}</dd>
          <dt>Организация</dt>
          <dd>{inputs?.organization || '—'}</dd>
          <dt>Местоположение</dt>
          <dd>{inputs?.location || '—'}</dd>
          <dt>Период работ</dt>
          <dd>
            {inputs?.start_date || inputs?.end_date
              ? `${fmtDate(inputs.start_date) || '…'} — ${fmtDate(inputs.end_date) || '…'}`
              : '—'}
          </dd>
          <dt>Сменность</dt>
          <dd>{inputs?.shifts || '—'}</dd>
          <dt>Группа грунтов (ГЭСН 05)</dt>
          <dd>{inputs?.soil_group ? `группа ${inputs.soil_group}` : 'не задана'}</dd>
          <dt>Особые условия</dt>
          <dd>{inputs?.conditions || '—'}</dd>
          <dt>РД</dt>
          <dd>
            {(inputs?.rd_documents ?? []).length === 0
              ? '—'
              : inputs.rd_documents.map((r) => (
                  <div key={r.revision_id}>
                    {r.document_code} — {r.title}, рев. {r.revision_no}
                    {r.rev_label ? ` (${r.rev_label})` : ''}
                  </div>
                ))}
          </dd>
          <dt>Техника</dt>
          <dd>
            {(inputs?.equipment ?? []).length === 0
              ? 'не указана'
              : inputs.equipment.map((e, i) => (
                  <div key={i}>
                    eq-{i + 1}: {e.name} ({equipmentKindLabel(e.kind)}) × {e.count}
                    {e.lift_capacity_t != null ? `, г/п ${fmtNum(e.lift_capacity_t)} т` : ''}
                    {e.load_chart.length > 0
                      ? `, ГВХ ${e.load_chart.length} ${pointsWord(e.load_chart.length)}`
                      : ''}
                    {(e.ai_fields ?? []).length > 0 ? (
                      <>
                        {' '}
                        <AiMarkTag details={aiDetails(e.ai_fields ?? [])} />
                      </>
                    ) : null}
                  </div>
                ))}
          </dd>
        </KvGrid>
      </details>

      <SubTitle>Сведения из РД</SubTitle>
      {groups.length === 0 ? (
        <Muted>Сведений нет.</Muted>
      ) : (
        groups.map(([kind, items]) => (
          <div key={kind} data-fact-kind={kind}>
            <Muted>
              <b>{FACT_KIND_LABEL[kind] ?? kind}</b> · {items.length}
            </Muted>
            {items.map((f) => (
              <Fact key={f.id} fact={f} />
            ))}
          </div>
        ))
      )}

      <SubTitle>Объёмы по РД</SubTitle>
      {(stats?.volumes_total ?? 0) > (facts?.volumes?.length ?? 0) && (
        <Muted data-ppr="volumes-truncated">
          Показаны первые {facts?.volumes?.length ?? 0} позиций из {stats?.volumes_total}; проверка
          грузоподъёмности выполнена по всем позициям.
        </Muted>
      )}
      {(facts?.volumes ?? []).length === 0 ? (
        <Muted>Позиций объёма в выбранных ревизиях нет.</Muted>
      ) : (
        <TableWrap $maxHeight="50vh">
          <DataTable>
            <thead>
              <tr>
                <th>id</th>
                <th>Наименование</th>
                <th>Обозначение</th>
                <th>Код</th>
                <th>Ед.</th>
                <th className="num">Кол-во</th>
                <th className="num">Масса ед., кг</th>
                <th>Источник</th>
              </tr>
            </thead>
            <tbody>
              {facts.volumes.map((v) => (
                <tr key={v.id}>
                  <td className="nowrap">{v.id}</td>
                  <td className="wrap">{v.name}</td>
                  <td>{v.designation ?? ''}</td>
                  <td className="nowrap">{v.code ?? ''}</td>
                  <td>{v.unit ?? ''}</td>
                  <td className="num">{fmtNum(v.quantity)}</td>
                  <td className="num">{fmtNum(v.unit_mass_kg)}</td>
                  <td className="nowrap">
                    {v.document_code}
                    {v.source_ref ? `, ${v.source_ref}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </TableWrap>
      )}

      <SubTitle>Расчёты (кодом)</SubTitle>
      {(facts?.calcs ?? []).length === 0 ? (
        <Muted>Расчётов нет.</Muted>
      ) : (
        facts.calcs.map((c) => {
          const st = CALC_STATUS[c.status] ?? { label: c.status, tone: 'neutral' as const };
          return (
            <FactItem key={c.id} data-calc={c.status}>
              <code>{c.id}</code>
              <Tag $tone={st.tone}>{st.label}</Tag>{' '}
              <b>
                {CALC_KIND_LABEL[c.kind] ?? c.kind}
                {c.equipment_name ? `: ${c.equipment_name}` : ''}
              </b>
              <div>{c.text}</div>
            </FactItem>
          );
        })
      )}

      <SubTitle>Нормативы (разрешённый перечень)</SubTitle>
      {(facts?.norms ?? []).length === 0 ? (
        <Muted>Перечень пуст.</Muted>
      ) : (
        <TableWrap $maxHeight="50vh">
          <DataTable>
            <thead>
              <tr>
                <th>id</th>
                <th>Шифр</th>
                <th>Наименование</th>
                <th>Откуда</th>
              </tr>
            </thead>
            <tbody>
              {facts.norms.map((n) => (
                <tr key={n.id}>
                  <td className="nowrap">{n.id}</td>
                  <td className="nowrap">{n.code}</td>
                  <td className="wrap">{n.title}</td>
                  <td>
                    {n.origin === 'rd' ? (
                      <Tag $tone="info">из РД{n.source_label ? `: ${n.source_label}` : ''}</Tag>
                    ) : (
                      <Tag $tone="neutral">базовый перечень</Tag>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </TableWrap>
      )}
    </div>
  );
}
